// Ed25519 签名 + canonical JSON（TypeScript 端）
//
// 端口 / 适配器：Signer / Verifier 是接口，默认实现走 @noble/ed25519。
// 同 Rust 端 canonical 一致，三端跨语言 byte-identical。

import * as ed from "@noble/ed25519";
import { sha512 } from "@noble/hashes/sha512";

// @noble/ed25519 v2 默认 async，注入 sha512 后启用 sync API
// （Node + 浏览器都受益）。一次性 setup，库内部判断即可。
(ed.etc as { sha512Sync?: (...m: Uint8Array[]) => Uint8Array }).sha512Sync = (
  ...msgs: Uint8Array[]
) => sha512(ed.etc.concatBytes(...msgs));

// ---------- canonical_json ----------

/**
 * 规范化 JSON：object key 字典序、无空白、与 Rust/Go 端 byte-identical。
 */
export function canonicalJson(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) throw new Error("non-finite number");
    return Number.isInteger(v) ? v.toString() : JSON.stringify(v);
  }
  if (typeof v === "string") return JSON.stringify(v);
  if (Array.isArray(v)) {
    return "[" + v.map(canonicalJson).join(",") + "]";
  }
  if (typeof v === "object") {
    const obj = v as Record<string, unknown>;
    const keys = Object.keys(obj).sort();
    return (
      "{" +
      keys
        .map((k) => JSON.stringify(k) + ":" + canonicalJson(obj[k]))
        .join(",") +
      "}"
    );
  }
  throw new Error(`unsupported value type: ${typeof v}`);
}

// ---------- Signer / Verifier 端口 ----------

export interface Signer {
  sign(msg: Uint8Array): Uint8Array;
}

export interface Verifier {
  verify(msg: Uint8Array, sig: Uint8Array): boolean;
}

// ---------- Ed25519 默认适配器 ----------

export class Ed25519Signer implements Signer {
  private constructor(private readonly priv: Uint8Array) {}

  static fromSeed(seed: Uint8Array): Ed25519Signer {
    if (seed.length !== 32) throw new Error("seed must be 32 bytes");
    return new Ed25519Signer(seed);
  }

  publicKey(): Uint8Array {
    return ed.getPublicKey(this.priv);
  }

  sign(msg: Uint8Array): Uint8Array {
    return ed.sign(msg, this.priv);
  }
}

export class Ed25519Verifier implements Verifier {
  private constructor(private readonly pub: Uint8Array) {}

  static fromPublicKey(pk: Uint8Array): Ed25519Verifier {
    if (pk.length !== 32) throw new Error("public key must be 32 bytes");
    return new Ed25519Verifier(pk);
  }

  verify(msg: Uint8Array, sig: Uint8Array): boolean {
    if (sig.length !== 64) return false;
    try {
      return ed.verify(sig, msg, this.pub);
    } catch {
      return false;
    }
  }
}

// ---------- hex 辅助（fixture 用）----------

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error("odd hex length");
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export function bytesToHex(b: Uint8Array): string {
  return Array.from(b)
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("");
}
